import React, { useState } from "react";
import { Copy, Check } from "lucide-react";
import type {
  BureauReportData,
  BureauAccountDetail,
  BureauHealthCategory,
} from "@/types/bureau.types";

interface Props {
  onClose: () => void;
  loading: boolean;
  error: string;
  data: BureauReportData | null;
  onRetry?: () => void;
}

const fmtMoney = (v?: number | null): string =>
  v === null || v === undefined ? "-" : `₹${Number(v).toLocaleString("en-IN")}`;

const fmtDate = (v?: string | null): string => {
  if (!v) return "-";
  try {
    return new Date(v).toLocaleDateString("en-IN", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    });
  } catch {
    return String(v);
  }
};

const scoreColor = (score?: number | null): string => {
  if (score === null || score === undefined) return "#6b7280";
  if (score >= 750) return "#16a34a";
  if (score >= 650) return "#ca8a04";
  return "#dc2626";
};

const ratingColor = (rating: string): string => {
  const r = rating.toLowerCase();
  if (r === "excellent" || r === "good") return "#16a34a";
  if (r === "fair" || r === "average") return "#ca8a04";
  return "#dc2626";
};

/**
 * Credit Details modal — renders the relevant slices of the Equifax bureau
 * report: score, personal info, credit summary, health breakdown, accounts
 * and recent enquiries.
 */
export const CreditDetailsModal: React.FC<Props> = ({
  onClose,
  loading,
  error,
  data,
  onRetry,
}) => {
  const creditDetails = data?.rawCreditReport?.creditDetails;
  const user = creditDetails?.userCreditDetails;
  const summary = creditDetails?.creditSummary;
  const accounts = creditDetails?.accountDetails ?? [];
  const enquiries = creditDetails?.enquiryHistory ?? [];
  const health = data?.creditHealthBreakdown;

  const fullName =
    [user?.firstName, user?.middleName, user?.lastName]
      .filter(Boolean)
      .join(" ")
      .trim() || "-";

  const healthCategories: BureauHealthCategory[] = health
    ? [
        health.paymentHistory,
        health.creditUtilization,
        health.creditAge,
        health.creditEnquiries,
        health.creditMix,
      ].filter((c): c is BureauHealthCategory => !!c)
    : [];

  return (
    <div onClick={onClose} style={overlay}>
      <div onClick={(e) => e.stopPropagation()} style={card}>
        <div style={headerRow}>
          <h2 style={{ margin: 0, fontSize: 18 }}>Credit Details</h2>
          <button onClick={onClose} style={closeBtn}>
            ×
          </button>
        </div>

        {loading && (
          <div style={{ padding: "40px 0", textAlign: "center", color: "#6b7280" }}>
            Fetching credit report…
          </div>
        )}

        {!loading && error && (
          <div style={{ padding: "24px 0", textAlign: "center" }}>
            <div style={{ color: "#dc2626", marginBottom: 12 }}>{error}</div>
            {onRetry && (
              <button onClick={onRetry} style={retryBtn}>
                Retry
              </button>
            )}
          </div>
        )}

        {!loading && !error && data && (
          <>
            {/* Score */}
            <div style={scoreRow}>
              <div>
                <div style={{ fontSize: 12, color: "#6b7280" }}>Credit Score</div>
                <div
                  style={{
                    fontSize: 32,
                    fontWeight: 700,
                    color: scoreColor(data.creditScore),
                  }}
                >
                  {data.creditScore ?? "-"}
                </div>
              </div>
              <div style={{ textAlign: "right", fontSize: 12, color: "#6b7280" }}>
                <div>Bureau: {user?.source || "equifax"}</div>
                {user?.expiredAt && <div>Valid till: {fmtDate(user.expiredAt)}</div>}
              </div>
            </div>

            {/* Health breakdown */}
            {healthCategories.length > 0 && (
              <Section title="Credit Health">
                <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                  {healthCategories.map((c) => (
                    <div key={c.category} style={healthRow}>
                      <div style={{ flex: 1 }}>
                        <div style={{ fontSize: 13, fontWeight: 600 }}>
                          {c.category}
                        </div>
                        <div style={{ fontSize: 12, color: "#6b7280" }}>
                          {c.description}
                        </div>
                      </div>
                      <span
                        style={{
                          ...ratingPill,
                          background: `${ratingColor(c.rating)}1a`,
                          color: ratingColor(c.rating),
                        }}
                      >
                        {c.rating}
                      </span>
                    </div>
                  ))}
                </div>
              </Section>
            )}

            {/* Personal info */}
            {user && (
              <Section title="Personal Information">
                <Grid>
                  <Field label="Name" value={fullName} copyable />
                  <Field label="Date of Birth" value={fmtDate(user.dateOfBirth)} copyable />
                  <Field label="Mobile" value={user.mobileNumber} copyable />
                  <Field label="PAN" value={user.pan} copyable />
                  <Field label="Email" value={user.emailAddress} copyable />
                  <Field label="Gender" value={user.gender} copyable />
                  <Field label="Total Income" value={fmtMoney(user.totalIncome)} copyable />
                  <Field
                    label="Address"
                    value={
                      [user.address, user.state, user.postal]
                        .filter(Boolean)
                        .join(", ") || "-"
                    }
                    wide
                    copyable
                  />
                </Grid>
              </Section>
            )}

            {/* Credit summary */}
            {summary && (
              <Section title="Credit Summary">
                <Grid>
                  <Field label="Total Accounts" value={summary.totalNoOfAccounts} />
                  <Field label="Active Accounts" value={summary.noOfActiveAccounts} />
                  <Field label="Open Loans" value={summary.totalOpenLoans} />
                  <Field label="Write-offs" value={summary.noOfWriteOffs} />
                  <Field
                    label="Total Outstanding"
                    value={fmtMoney(summary.totalBalanceAmount)}
                  />
                  <Field
                    label="Total Sanctioned"
                    value={fmtMoney(summary.totalSanctionAmount)}
                  />
                  <Field
                    label="Monthly EMI Total"
                    value={fmtMoney(summary.totalMonthlyPaymentAmount)}
                  />
                  <Field label="Total Past Due" value={fmtMoney(summary.totalPastDue)} />
                  <Field
                    label="Enquiries (12M)"
                    value={summary.inquiriesInPast12Month}
                  />
                  <Field
                    label="Max DPD (12M)"
                    value={
                      summary.maxDPD12M !== null && summary.maxDPD12M !== undefined
                        ? `${summary.maxDPD12M} days`
                        : "-"
                    }
                  />
                  <Field
                    label="Credit Vintage"
                    value={
                      summary.creditVintageInMonths !== null &&
                      summary.creditVintageInMonths !== undefined
                        ? `${Math.floor(summary.creditVintageInMonths / 12)}y ${
                            summary.creditVintageInMonths % 12
                          }m`
                        : "-"
                    }
                  />
                  <Field label="Oldest Account" value={fmtDate(summary.oldestAccount)} />
                </Grid>
              </Section>
            )}

            {/* Accounts */}
            {accounts.length > 0 && (
              <Section title={`Accounts (${accounts.length})`}>
                <div style={{ overflowX: "auto" }}>
                  <table style={tableStyle}>
                    <thead>
                      <tr>
                        {["Institution", "Type", "Sanctioned", "Balance", "EMI", "Status", "Opened"].map(
                          (h) => (
                            <th key={h} style={thStyle}>
                              {h}
                            </th>
                          )
                        )}
                      </tr>
                    </thead>
                    <tbody>
                      {accounts.map((a: BureauAccountDetail, i: number) => (
                        <tr key={i}>
                          <td style={tdStyle}>{a.institution || "-"}</td>
                          <td style={tdStyle}>{a.accountType || "-"}</td>
                          <td style={tdStyle}>{fmtMoney(a.sanctionAmount)}</td>
                          <td style={tdStyle}>{fmtMoney(a.balance)}</td>
                          <td style={tdStyle}>{fmtMoney(a.installmentAmount)}</td>
                          <td style={tdStyle}>
                            <span
                              style={{
                                ...statusPill,
                                background: a.isOpen ? "#dcfce7" : "#f3f4f6",
                                color: a.isOpen ? "#15803d" : "#6b7280",
                              }}
                            >
                              {a.isOpen ? "Active" : "Closed"}
                            </span>
                          </td>
                          <td style={tdStyle}>{fmtDate(a.dateOpened)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </Section>
            )}

            {/* Enquiries */}
            {enquiries.length > 0 && (
              <Section title={`Recent Enquiries (${enquiries.length})`}>
                <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                  {enquiries.map((e, i) => (
                    <div key={i} style={enquiryRow}>
                      <span style={{ fontSize: 13 }}>
                        {e.enquiryInstitution || "-"}
                      </span>
                      <span style={{ fontSize: 12, color: "#6b7280" }}>
                        {fmtDate(e.enquiryDate)}
                      </span>
                    </div>
                  ))}
                </div>
              </Section>
            )}
          </>
        )}
      </div>
    </div>
  );
};

const Section: React.FC<{ title: string; children: React.ReactNode }> = ({
  title,
  children,
}) => (
  <div style={{ marginTop: 16 }}>
    <div
      style={{
        fontSize: 13,
        fontWeight: 700,
        marginBottom: 8,
        color: "#111827",
        borderBottom: "1px solid #e5e7eb",
        paddingBottom: 6,
      }}
    >
      {title}
    </div>
    {children}
  </div>
);

const Grid: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <div
    style={{
      display: "grid",
      gridTemplateColumns: "1fr 1fr",
      gap: "8px 16px",
    }}
  >
    {children}
  </div>
);

const Field: React.FC<{
  label: string;
  value?: string | number | null;
  wide?: boolean;
  copyable?: boolean;
}> = ({ label, value, wide, copyable }) => {
  const [copied, setCopied] = useState(false);
  const display =
    value === null || value === undefined || value === "" ? "-" : value;

  const handleCopy = () => {
    navigator.clipboard.writeText(String(value));
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  };

  return (
    <div style={wide ? { gridColumn: "1 / -1" } : undefined}>
      <div style={{ fontSize: 11, color: "#6b7280" }}>{label}</div>
      <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
        <div style={{ fontSize: 13, fontWeight: 500, wordBreak: "break-word" }}>
          {display}
        </div>
        {copyable && display !== "-" && (
          <button
            onClick={handleCopy}
            title={copied ? "Copied!" : `Copy ${label}`}
            style={copyBtn}
          >
            {copied ? <Check size={13} color="#16a34a" /> : <Copy size={13} />}
          </button>
        )}
      </div>
    </div>
  );
};

const overlay: React.CSSProperties = {
  position: "fixed",
  inset: 0,
  background: "rgba(0,0,0,0.5)",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  zIndex: 1000,
};
const card: React.CSSProperties = {
  background: "#fff",
  borderRadius: 12,
  padding: 24,
  width: 720,
  maxWidth: "95vw",
  color: "#111",
  maxHeight: "90vh",
  overflowY: "auto",
};
const headerRow: React.CSSProperties = {
  display: "flex",
  justifyContent: "space-between",
  alignItems: "center",
  marginBottom: 8,
};
const closeBtn: React.CSSProperties = {
  border: "none",
  background: "none",
  fontSize: 20,
  cursor: "pointer",
};
const scoreRow: React.CSSProperties = {
  display: "flex",
  justifyContent: "space-between",
  alignItems: "flex-end",
  padding: "12px 16px",
  background: "#f9fafb",
  borderRadius: 8,
};
const healthRow: React.CSSProperties = {
  display: "flex",
  alignItems: "center",
  gap: 12,
};
const ratingPill: React.CSSProperties = {
  fontSize: 11,
  fontWeight: 600,
  padding: "3px 10px",
  borderRadius: 999,
  whiteSpace: "nowrap",
};
const statusPill: React.CSSProperties = {
  fontSize: 11,
  fontWeight: 600,
  padding: "3px 10px",
  borderRadius: 999,
  whiteSpace: "nowrap",
};
const tableStyle: React.CSSProperties = {
  width: "100%",
  borderCollapse: "collapse",
  fontSize: 12,
};
const thStyle: React.CSSProperties = {
  textAlign: "left",
  padding: "6px 8px",
  borderBottom: "1px solid #e5e7eb",
  color: "#6b7280",
  fontWeight: 600,
  whiteSpace: "nowrap",
};
const tdStyle: React.CSSProperties = {
  padding: "6px 8px",
  borderBottom: "1px solid #f3f4f6",
  verticalAlign: "top",
};
const enquiryRow: React.CSSProperties = {
  display: "flex",
  justifyContent: "space-between",
  gap: 12,
};
const copyBtn: React.CSSProperties = {
  border: "none",
  background: "none",
  padding: 2,
  cursor: "pointer",
  color: "#6b7280",
  display: "inline-flex",
  alignItems: "center",
  flexShrink: 0,
};
const retryBtn: React.CSSProperties = {
  padding: "8px 16px",
  borderRadius: 8,
  border: "none",
  background: "#1f2d3d",
  color: "#fff",
  cursor: "pointer",
};
